import * as React from 'react';
import DocumentTitle from 'react-document-title';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
// eslint-disable-next-line no-restricted-imports
import styled, { css } from 'styled-components';

import AkAvatar from '@atlaskit/avatar/avatar';
import { cssMap } from '@atlaskit/css';
import AkGrid from '@atlaskit/page/grid';
import { GridColumn as AkGridColumn } from '@atlaskit/page/grid-column';
import { Box as AkBox } from '@atlaskit/primitives/box';
import { xcss as akXcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';

import { ErrorBoundary } from '@adminhub/error-boundary';
import { PageHeader } from '@adminhub/responsive/ui/page-header';
import { useOrgName, useSiteName } from '@adminhub/router';
import { Centered } from '@adminhub/styled';

import { useMainLayoutRightSidebar } from '../hooks/use-main-layout-right-sidebar';
import { formatDocumentTitle } from '../utils/format-document-title';
import { adminMessage } from '../utils/messages';
import type { AddedProps } from './add-collapsed-nav-prop';
import { addCollapsedNavProp } from './add-collapsed-nav-prop';
import { InlineEditTitle, type InlineEditTitleAs } from './inline-edit-title';

const styles = cssMap({
	isDisabledColor: {
		color: token('color.text.disabled'),
	},
});

const Subtitle = styled.span`
	font-style: italic;
	font-size: ${token('font.body')};
	${({ isDisabled }: { isDisabled: boolean }) =>
		isDisabled
			? css`
					color: ${token('color.text.disabled')};
				`
			: ''};
`;

const PageBannerWrapper = styled.div`
	margin-top: ${token('space.100')};
	margin-bottom: ${token('space.negative.050')};
`;

const Main = styled.div<{ preventContentOverflow: boolean }>`
	padding-bottom: ${token('space.400')};
	padding-left: ${token('space.300')};
	padding-right: ${token('space.300')};
	contain: ${({ preventContentOverflow }) => (preventContentOverflow ? 'paint' : 'unset')};
`;

const Icon = styled.div`
	line-height: 1;
	margin-right: ${token('space.100')};
	${({ isDisabled }: { isDisabled: boolean }) =>
		isDisabled
			? css`
					filter: grayscale(100%);
				`
			: ''};
`;

const Description = styled.div`
	max-width: 900px;
	margin-top: ${token('space.negative.100')};
`;

const Avatar = styled.div`
	padding-right: ${token('space.150')};
	float: left;
	${({ isDisabled }: { isDisabled: boolean }) =>
		isDisabled
			? css`
					filter: grayscale(100%);
				`
			: ''} & > div {
		display: initial !important;
	}
`;

const Children: React.FC<React.PropsWithChildren<{ xcss?: ReturnType<typeof akXcss> }>> = ({
	children,
	xcss,
}) => {
	const defaultChildrenXcss = akXcss({
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		marginTop: 'space.300',
	});

	return <AkBox xcss={[defaultChildrenXcss, xcss]}>{children}</AkBox>;
};

const AssessmentsLayoutWrapper = styled.div<{ heightCss: string }>`
	height: ${({ heightCss }) => heightCss};
	overflow: auto;
`;

const CustomLayoutHeightWrapper: React.FC<
	React.PropsWithChildren<{
		heightCss?: string;
		children: React.ReactNode;
	}>
> = ({ heightCss, children }) => {
	if (!heightCss) {
		return <>{children}</>;
	}

	return <AssessmentsLayoutWrapper heightCss={heightCss}>{children}</AssessmentsLayoutWrapper>;
};

export type PageType = 'skeleton' | 'error';

export interface MainLayoutOwnProps {
	id: string;
	orgNameForSite?: string;
	pageType?: PageType;
	title?: React.ReactNode;
	inlineEdit?: {
		defaultValue: string;
		errorMessage?: string | null;
		onConfirm(value: string): void;
		/**
		 * HTML element / heading level for the editable title. Defaults to the level
		 * inferred from the heading size. Pass `as="h1"` when the title is the page's
		 * top-level heading.
		 */
		as?: InlineEditTitleAs;
	};
	isDisabled?: boolean;
	subtitle?: any;
	children?: React.ReactNode;
	description?: any;
	side?: any;
	isFullWidth?: boolean;
	isAutoGridSize?: boolean;
	// Pass a URL to load an avatar
	avatarIconUrl?: string;
	// Pass AkIcon's to be rendered to the left of the title
	icon?: React.ReactNode;
	action?: React.ReactElement;
	breadcrumbs?: React.ReactElement;
	pageParent?: React.ReactNode;
	pageBanner?: React.ReactNode;
	gridLayout?: 'fixed' | 'fluid';
	// Accepts both RefObject and callback refs; cast to any at the innerRef
	// call-site below since styled-components v4 types don't expose Ref<T>.
	pageRef?: React.Ref<HTMLElement>;
	testId?: string;
	childrenContainerStyle?: ReturnType<typeof akXcss>;
	/**
	 * This value controls the CSS property "contain: paint". It is useful for hiding overflowing content.
	 */
	preventContentOverflow?: boolean;
	/**
	 * Use this prop to render a right sidebar from AUK (@atlassian/sidebar).
	 * Use the useMainLayoutRightSidebar() hook to control the visibility of this sidebar.
	 */
	rightSidebar?: Readonly<React.ReactNode>;
	showTitleOnPage?: boolean;
	/**
	 * Use this prop to ensure tables do not take up full width of page.
	 */
	excludeTableFullWidth?: boolean;
	mainContentHeightCss?: string;
	disableTitleStyles?: boolean;
	contentAboveDescription?: React.ReactNode;
	errorFallback?: JSX.Element;
	errorCallback?(result: { error: Error }): void;
}

export type MainLayoutProps = MainLayoutOwnProps & AddedProps;

export const MainLayoutImpl: React.FC<MainLayoutOwnProps> = ({
	orgNameForSite,
	title,
	isDisabled = false,
	subtitle,
	action,
	description,
	children,
	side,
	breadcrumbs,
	pageParent,
	pageBanner,
	isAutoGridSize,
	isFullWidth,
	inlineEdit,
	gridLayout,
	pageRef,
	testId = 'main-layout',
	childrenContainerStyle,
	preventContentOverflow = false,
	rightSidebar,
	avatarIconUrl,
	icon,
	showTitleOnPage = true,
	excludeTableFullWidth = false,
	mainContentHeightCss,
	disableTitleStyles = false,
	contentAboveDescription,
	errorFallback,
	errorCallback,
}) => {
	const { hideSidebar } = useMainLayoutRightSidebar();
	React.useEffect(
		() => () => {
			hideSidebar();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const asidePortalEl = document.getElementById('aside-portal');

	const determineChildGridSize = (): number => {
		if (side || !isFullWidth) {
			return 8;
		}

		return 12;
	};

	const renderIcon = (): React.ReactNode => {
		if (avatarIconUrl) {
			return (
				<Avatar isDisabled={isDisabled || false} data-testid="avatar-image">
					<AkAvatar src={avatarIconUrl} size="large" />
				</Avatar>
			);
		}

		if (icon) {
			return <Icon isDisabled={isDisabled || false}>{icon}</Icon>;
		}

		return undefined;
	};

	const onInlineEditConfirm = (value: string) => {
		inlineEdit?.onConfirm(value);
	};

	// Extracts a string from a React node if it's a string
	const extractStringFromElement = (element: React.ReactNode): string => {
		if (typeof element === 'string') {
			return element;
		}

		return '';
	};

	// Extracts strings from an array of React nodes and concatenates them
	const extractStringFromArrayOfElements = (element: React.ReactNode): string => {
		if (Array.isArray(element)) {
			return element
				.map(extractStringFromElementRecursively)
				.filter((message) => message)
				.join(' ');
		}

		return '';
	};

	// Extracts a string from the props of a React element
	const extractStringFromElementProps = (props: {
		children?: React.ReactNode;
		defaultMessage?: string;
		isCustomTitle?: boolean;
		name?: string;
		productName?: string;
		displayName?: string;
	}): string => {
		if (props.isCustomTitle) {
			return props.name || props.productName || props.displayName || '';
		}

		// When a container element (e.g. Stack) wraps multiple children, props.children
		// is an array. extractStringFromElementRecursively only handles single elements,
		// so delegate to extractStringFromArrayOfElements for arrays.
		if (Array.isArray(props.children)) {
			return extractStringFromArrayOfElements(props.children);
		}

		return extractStringFromElementRecursively(props.children);
	};

	// Recursively extracts a string from a React node
	const extractStringFromElementRecursively = (element: React.ReactNode): string => {
		// Return plain-string children as-is so a title wrapped in an element — e.g.
		// <Flex>{'My title'}<Tag/></Flex> — isn't dropped (A11Y-39308 / WCAG 2.4.2).
		if (typeof element === 'string') {
			return element;
		}

		if (React.isValidElement(element)) {
			const props = element.props as {
				children?: React.ReactNode;
				defaultMessage?: string;
				isCustomTitle?: boolean;
				name?: string;
				productName?: string;
				displayName?: string;
			};
			if (
				(typeof element.type === 'function' || typeof element.type === 'object') &&
				'displayName' in element.type &&
				(element.type.displayName === 'FormattedMessage' ||
					element.type.displayName === 'MemoizedFormattedMessage')
			) {
				return props.defaultMessage || '';
			}

			return extractStringFromElementProps(props);
		}

		return '';
	};

	// Main function to extract a title string from a React node
	const extractTitleString = (element: React.ReactNode): string => {
		if (!element) {
			return '';
		}

		return (
			extractStringFromElement(element) ||
			extractStringFromArrayOfElements(element) ||
			extractStringFromElementRecursively(element)
		);
	};

	const extractedTitle = extractTitleString(title);
	const extractedParentTitle = extractTitleString(pageParent);
	const siteName = useSiteName();
	const orgName = useOrgName() || orgNameForSite;
	const adminTag = <FormattedMessage {...adminMessage} />;
	const admin = adminTag?.props?.defaultMessage && `${adminTag?.props?.defaultMessage}`;
	const docTitle = () =>
		formatDocumentTitle(extractedTitle, extractedParentTitle, siteName, orgName, admin);

	// Updating the page title to be more specific depending on the path of the page.
	return (
		<ErrorBoundary fallback={errorFallback} onError={errorCallback}>
			<div>
				<CustomLayoutHeightWrapper heightCss={mainContentHeightCss}>
					{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
					<Main
						innerRef={pageRef as any}
						data-testid={testId}
						preventContentOverflow={preventContentOverflow}
					>
						<DocumentTitle title={docTitle()} data-testid="document-title" />
						<AkGrid
							spacing={!excludeTableFullWidth ? 'cosy' : 'comfortable'}
							layout={!excludeTableFullWidth ? 'fluid' : gridLayout}
						>
							{
								!side && !isFullWidth && (
									<AkGridColumn medium={2} />
								) /* adds blank space for sidebar */
							}
							{pageBanner && (
								<AkGridColumn medium={12}>
									<PageBannerWrapper data-testid="pageBanner">{pageBanner}</PageBannerWrapper>
								</AkGridColumn>
							)}
							<AkGridColumn medium={isAutoGridSize ? undefined : determineChildGridSize()}>
								<PageHeader
									bottomBar={subtitle && <Subtitle isDisabled={isDisabled}>{subtitle}</Subtitle>}
									actions={action}
									breadcrumbs={breadcrumbs}
									disableTitleStyles={!!inlineEdit || disableTitleStyles}
								>
									<Centered axis="vertical">
										{renderIcon()}
										{showTitleOnPage && title && !inlineEdit && (
											<span css={[isDisabled && styles.isDisabledColor]}>{title}</span>
										)}
										{inlineEdit && (
											<InlineEditTitle
												defaultValue={inlineEdit.defaultValue}
												errorMessage={inlineEdit.errorMessage}
												onConfirm={onInlineEditConfirm}
												as={inlineEdit.as}
											/>
										)}
									</Centered>
								</PageHeader>
								{contentAboveDescription || null}
								{description && <Description>{description}</Description>}
								{children && <Children xcss={childrenContainerStyle}>{children}</Children>}
								{rightSidebar && asidePortalEl && createPortal(rightSidebar, asidePortalEl)}
							</AkGridColumn>
							{side && (
								<AkGridColumn medium={4}>
									<Children>{side}</Children>
								</AkGridColumn>
							)}
						</AkGrid>
					</Main>
				</CustomLayoutHeightWrapper>
			</div>
		</ErrorBoundary>
	);
};

export const MainLayout: React.ComponentClass<MainLayoutProps & React.RefAttributes<HTMLElement>> =
	addCollapsedNavProp(
		React.forwardRef((props: MainLayoutOwnProps, ref: React.RefObject<HTMLElement>) => (
			<MainLayoutImpl pageRef={ref} {...props} />
		)),
	);
MainLayout.displayName = 'MainLayout';
